import React, { useState, useCallback } from 'react';

type NpmImportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onImport: (files: Record<string, string>) => void;
};

type NpmPackageInfo = {
  name: string;
  version: string;
  description?: string;
  main?: string;
  types?: string;
  repository?: {
    type: string;
    url: string;
  };
};

export const NpmImportModal = React.memo(function NpmImportModal({
  isOpen,
  onClose,
  onImport,
}: NpmImportModalProps) {
  const [packageName, setPackageName] = useState('');
  const [version, setVersion] = useState('latest');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [packageInfo, setPackageInfo] = useState<NpmPackageInfo | null>(null);

  // Fetch package info
  const fetchPackageInfo = useCallback(async () => {
    if (!packageName.trim()) {
      setError('Please enter a package name');
      return;
    }

    setLoading(true);
    setError(null);
    setProgress('Fetching package info...');
    setPackageInfo(null);

    try {
      // Fetch package metadata from npm registry
      const registryUrl = `https://registry.npmjs.org/${encodeURIComponent(packageName)}`;
      const response = await fetch(registryUrl);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Package "${packageName}" not found`);
        }
        throw new Error(`Failed to fetch package: ${response.statusText}`);
      }

      const data = await response.json();
      const versionToUse = version === 'latest' ? data['dist-tags']?.latest : version;

      if (!versionToUse || !data.versions?.[versionToUse]) {
        throw new Error(`Version "${version}" not found`);
      }

      const versionData = data.versions[versionToUse];
      setPackageInfo({
        name: versionData.name,
        version: versionToUse,
        description: versionData.description,
        main: versionData.main,
        types: versionData.types || versionData.typings,
        repository: versionData.repository,
      });
      setProgress(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch package info');
      setProgress(null);
    } finally {
      setLoading(false);
    }
  }, [packageName, version]);

  // Import package files via unpkg
  const handleImport = useCallback(async () => {
    if (!packageInfo) return;

    setLoading(true);
    setError(null);
    setProgress('Fetching package files...');

    try {
      const files: Record<string, string> = {};
      const baseUrl = `https://unpkg.com/${packageInfo.name}@${packageInfo.version}`;

      // Common files to fetch
      const filesToFetch = [
        packageInfo.main || 'index.js',
        'package.json',
        'README.md',
        'LICENSE',
      ];

      // If types exist, add them
      if (packageInfo.types) {
        filesToFetch.push(packageInfo.types);
      }

      // Try to fetch directory listing
      setProgress('Scanning package files...');
      const dirResponse = await fetch(`${baseUrl}/?meta`);

      if (dirResponse.ok) {
        const dirData = await dirResponse.json();

        // Recursively collect all file paths
        const collectFiles = (node: { path: string; type: string; files?: unknown[] }, basePath = ''): string[] => {
          const paths: string[] = [];
          if (node.type === 'file') {
            paths.push(node.path);
          } else if (node.type === 'directory' && node.files) {
            for (const child of node.files as { path: string; type: string; files?: unknown[] }[]) {
              paths.push(...collectFiles(child, node.path));
            }
          }
          return paths;
        };

        const allFiles = collectFiles(dirData);

        // Filter to only include source files
        const sourceExts = ['.js', '.ts', '.jsx', '.tsx', '.json', '.md', '.css', '.scss', '.less'];
        const sourceFiles = allFiles.filter(f => {
          const ext = f.slice(f.lastIndexOf('.'));
          return sourceExts.includes(ext) && !f.includes('node_modules');
        });

        // Limit to first 100 files to avoid overwhelming the IDE
        const filesToDownload = sourceFiles.slice(0, 100);

        let downloaded = 0;
        for (const filePath of filesToDownload) {
          setProgress(`Downloading: ${filePath} (${downloaded + 1}/${filesToDownload.length})`);

          try {
            const fileUrl = `https://unpkg.com/${packageInfo.name}@${packageInfo.version}${filePath}`;
            const fileResponse = await fetch(fileUrl);

            if (fileResponse.ok) {
              const content = await fileResponse.text();
              // Remove leading slash from path
              const normalizedPath = filePath.startsWith('/') ? filePath.slice(1) : filePath;
              files[`${packageInfo.name}/${normalizedPath}`] = content;
              downloaded++;
            }
          } catch {
            // Skip files that fail to download
          }
        }
      } else {
        // Fallback: try to fetch common files individually
        for (const filePath of filesToFetch) {
          setProgress(`Fetching: ${filePath}`);
          try {
            const fileUrl = `${baseUrl}/${filePath}`;
            const fileResponse = await fetch(fileUrl);

            if (fileResponse.ok) {
              const content = await fileResponse.text();
              files[`${packageInfo.name}/${filePath}`] = content;
            }
          } catch {
            // Skip files that fail to download
          }
        }
      }

      if (Object.keys(files).length === 0) {
        throw new Error('No files could be downloaded from this package');
      }

      setProgress(`Imported ${Object.keys(files).length} files`);
      onImport(files);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import package');
    } finally {
      setLoading(false);
      setProgress(null);
    }
  }, [packageInfo, onImport]);

  const handleClose = useCallback(() => {
    setError(null);
    setProgress(null);
    setPackageInfo(null);
    onClose();
  }, [onClose]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      if (!packageInfo) {
        fetchPackageInfo();
      } else {
        handleImport();
      }
    }
  }, [loading, packageInfo, fetchPackageInfo, handleImport]);

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div
        className="modal npm-import-modal"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        style={{ maxWidth: '500px' }}
      >
        <div className="modal-header">
          <div className="modal-title">Import from npm</div>
          <button className="modal-close" onClick={handleClose}>
            x
          </button>
        </div>

        <div className="modal-body" style={{ padding: '16px' }}>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 2 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                Package name:
              </label>
              <input
                type="text"
                value={packageName}
                onChange={(e) => {
                  setPackageName(e.target.value);
                  setPackageInfo(null);
                }}
                placeholder="react"
                style={{
                  width: '100%',
                  padding: '8px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  color: 'var(--text)',
                  fontSize: '13px',
                }}
                autoFocus
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                Version:
              </label>
              <input
                type="text"
                value={version}
                onChange={(e) => {
                  setVersion(e.target.value);
                  setPackageInfo(null);
                }}
                placeholder="latest"
                style={{
                  width: '100%',
                  padding: '8px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  color: 'var(--text)',
                  fontSize: '13px',
                }}
              />
            </div>
          </div>

          {!packageInfo && !loading && (
            <button
              className="pill-btn"
              onClick={fetchPackageInfo}
              disabled={!packageName.trim()}
              style={{
                width: '100%',
                marginBottom: '16px',
                opacity: !packageName.trim() ? 0.5 : 1,
              }}
            >
              Look up package
            </button>
          )}

          {packageInfo && (
            <div style={{
              padding: '12px',
              background: 'var(--bg-secondary)',
              borderRadius: '4px',
              marginBottom: '16px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontWeight: 'bold' }}>{packageInfo.name}</span>
                <span style={{ fontSize: '12px', color: 'var(--muted)' }}>v{packageInfo.version}</span>
              </div>
              {packageInfo.description && (
                <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>
                  {packageInfo.description}
                </div>
              )}
              <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                Main: {packageInfo.main || 'index.js'}
                {packageInfo.types && ` | Types: ${packageInfo.types}`}
              </div>
            </div>
          )}

          {progress && (
            <div style={{
              padding: '12px',
              background: 'var(--bg-secondary)',
              borderRadius: '4px',
              marginBottom: '12px',
              fontSize: '12px',
              color: 'var(--muted)'
            }}>
              {progress}
            </div>
          )}

          {error && (
            <div style={{ color: 'var(--error)', fontSize: '12px', marginBottom: '12px' }}>
              {error}
            </div>
          )}

          <div style={{
            padding: '12px',
            background: 'var(--bg-secondary)',
            borderRadius: '4px',
            fontSize: '11px',
            color: 'var(--muted)'
          }}>
            <strong>Note:</strong> Package source files will be downloaded via unpkg CDN.
            Large packages may be limited to 100 files. Binary files are skipped.
          </div>
        </div>

        <div className="modal-footer">
          <button className="pill-btn" onClick={handleClose}>
            Cancel
          </button>
          {packageInfo && (
            <button
              className="pill-btn"
              onClick={handleImport}
              disabled={loading}
              style={{ opacity: loading ? 0.5 : 1 }}
            >
              {loading ? 'Importing...' : 'Import Package'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

export default NpmImportModal;
